import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { NavIcon } from '../../NavIcon';
import { Dialog, DialogContentBare, DialogTitleBare } from '@/components/Dialog';
import { Column } from '@/components/Column';
import { Row } from '@/components/Row';
import { useNavigate } from 'react-router-dom';

import { NotificationHeader } from './NotificationHeader';
import { RailBellIcon, railIconButtonClass, railUnreadDotClass } from './railTrigger';
import { BookingNotification } from './BookingNotification';
import { ApiNotification, isApiNotificationVisible } from './ApiNotification';
import { ProfessionalReviewHost, useProfessionalReviewDialog } from './ProfessionalReviewHost';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { NotificationEmptyState } from './NotificationEmptyState';
import { useNotifications } from './useNotifications';
import { useWebSocket } from '@/contexts/websocket';
import { mergeNotificationsByDate, realtimeNotificationKey } from './mergeNotifications';
import { TOUCH_HIT_AREA } from '@/utils/touchTarget';
import { RailTooltip } from '@/components/Rail/components/RailTooltip';
import { ActionDialog } from '@/containers/Activities/components/ActionDialog';

const ConfirmationModal = lazy(() =>
  import('@/components/ConfirmationModal/ConfirmationModal').then((module) => ({
    default: module.ConfirmationModal
  }))
);

type NotificationsProps = {
  variant?: 'classic' | 'topbar' | 'rail';
  /** Whether the parent rail is in collapsed (icon-only) mode. Only used by the `rail` variant. */
  collapsed?: boolean;
};

export const Notifications: React.FC<NotificationsProps> = ({ variant = 'classic', collapsed = false }) => {
  const navigate = useNavigate();
  const [showDropdownMenu, setShowDropdownMenu] = useState<boolean>(false);
  const [showFullView, setShowFullView] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640;
    }
    return false;
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  // La reseña de un turno suyo (profesional del equipo). «Ver turno» abre el mismo panel de reservas.
  const professionalReview = useProfessionalReviewDialog();
  const { showConfirmation, setShowConfirmation, mutation } = useNotifications();

  const {
    apiNotifications: allApiNotifications,
    totalUnreadCount,
    markApiNotificationAsRead,
    markAllApiNotificationsAsRead,
    archiveApiNotification,
    markAllAsRead: markAllWebSocketAsRead,
    notifications: realtimeNotifications,
    removeRealtimeNotification
  } = useWebSocket();
  const flags = useFeatureFlags();
  const apiNotifications = allApiNotifications.filter((n) => isApiNotificationVisible(n, flags));

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleNotificationClick = () => {
    if (isMobile) {
      setShowFullView(true);
      if (totalUnreadCount > 0) {
        markAllWebSocketAsRead();
        markAllApiNotificationsAsRead();
      }
    } else {
      handleDropdownOpen(!showDropdownMenu);
    }
  };

  const handleDropdownOpen = (open: boolean) => {
    setShowDropdownMenu(open);
    if (open && totalUnreadCount > 0) {
      markAllWebSocketAsRead();
      markAllApiNotificationsAsRead();
    }
  };

  const handleBackClick = () => {
    setShowFullView(false);
  };

  const handleOpenSideMenu = (bookingId: number) => {
    setSelectedBookingId(bookingId);
  };

  const handleCloseSideMenu = () => {
    setSelectedBookingId(null);
  };

  const hasAnyNotifications = realtimeNotifications.length > 0 || apiNotifications.length > 0;
  const totalCount = realtimeNotifications.length + apiNotifications.length;

  const isTopbar = variant === 'topbar';
  const isRail = variant === 'rail';

  const triggerButtonClass = isRail
    ? railIconButtonClass(collapsed)
    : isTopbar
      ? // Campana suelta, sin caja: 40 px a la vista, 44 px al toque.
        `relative grid h-10 w-10 place-items-center rounded-lg text-frost-ink transition-colors duration-200 hover:bg-[rgba(var(--f-ink-rgb),0.05)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30 ${TOUCH_HIT_AREA}`
      : 'font-book text-sm transition-colors duration-200 relative hover:text-primary p-2 rounded-full text-frost-ink-soft hover:bg-frost-paper';

  const bellSizeClass = isTopbar ? '' : 'text-[1.25em]';
  const bellClass = isTopbar
    ? 'text-fs-15 transition-transform duration-200'
    : 'transition-transform duration-200';
  const badgeClass = isTopbar
    ? ''
    : 'absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 text-fs-10 font-extrabold leading-none text-white bg-frost-coral rounded-full min-w-[18px] h-[18px] shadow-[0_2px_6px_rgba(var(--f-coral-rgb),0.45)] ring-2 ring-frost-card';

  // Punto quieto (sin pulso): avisa que hay algo nuevo sin moverse.
  const topbarDot = (
    <span
      aria-hidden
      className="absolute top-[7px] right-[8px] h-[7px] w-[7px] rounded-full bg-frost-coral ring-2 ring-frost-card"
    />
  );

  const railDot = <span aria-hidden className={railUnreadDotClass} />;

  // En el riel la campana es solo ícono: el nombre sale en el globo oscuro, no en el `title` nativo.
  const withRailTooltip = (node: React.ReactElement) =>
    isRail ? (
      <RailTooltip label="Notificaciones" enabled>
        {node}
      </RailTooltip>
    ) : (
      node
    );

  const renderTriggerInner = () => (
    <>
      {isRail ? (
        <RailBellIcon collapsed={collapsed} />
      ) : (
        <NavIcon name="bell" className={`${bellSizeClass} ${bellClass}`} />
      )}
      {totalUnreadCount > 0 &&
        (isTopbar ? (
          topbarDot
        ) : isRail ? (
          railDot
        ) : (
          <span className={badgeClass}>{totalUnreadCount > 99 ? '99+' : totalUnreadCount}</span>
        ))}
    </>
  );

  const confirmationItems = {
    title: (
      <>
        <p>Instrucciones para el pago:</p>
      </>
    ),
    content: (
      <Column justify="flex-start" className="p-2">
        <Row className="text-left">Transferir al alias: AGORA.ARG</Row>
        <Row className="text-left pt-2">
          Notificar luego de haber realizado la transferencia mediante el botón debajo
        </Row>
        <Row className="text-left pt-2">
          Por cualquier duda nos pueden escribir por WhatsApp al{' '}
          <a href="https://api.whatsapp.com/send/?phone=541160204078" target="_blank" rel="noreferrer">
            11 6020-4078
          </a>
        </Row>
      </Column>
    ),
    confirmText: 'Notificar'
  };

  // ----- Shared notification list -----
  // Tiempo real y API mezclados por fecha: ver `mergeNotificationsByDate`.
  const renderList = (variant: 'dropdown' | 'sheet') => {
    const merged = mergeNotificationsByDate(realtimeNotifications, apiNotifications);
    return merged.map((item, idx) => {
      const isLast = idx === merged.length - 1;
      if (item.kind === 'realtime') {
        const realtimeIndex = item.index;
        return (
          <BookingNotification
            key={realtimeNotificationKey(item.notification, realtimeIndex)}
            notification={item.notification}
            index={idx}
            isLast={isLast}
            variant={variant}
            onClick={handleOpenSideMenu}
            onArchive={() => removeRealtimeNotification(realtimeIndex)}
          />
        );
      }
      return (
        <ApiNotification
          key={`api-${item.notification.id}`}
          notification={item.notification}
          onMarkAsRead={markApiNotificationAsRead}
          onArchive={archiveApiNotification}
          onClick={handleOpenSideMenu}
          onOpenProfessionalReview={professionalReview.openReview}
          index={idx}
          isLast={isLast}
          variant={variant}
        />
      );
    });
  };

  const professionalReviewHost = (
    <ProfessionalReviewHost
      state={professionalReview.state}
      hidden={selectedBookingId !== null}
      onClose={professionalReview.closeReview}
      onOpenBooking={handleOpenSideMenu}
    />
  );

  // ----- Mobile full-screen view -----
  // Es una pantalla completa que tapa la app: va por el Dialog del sistema (foco
  // atrapado, Esc y "Volver" cierran, el scroll de atrás queda quieto) en la misma
  // capa de siempre, `z-takeover`.
  const mobileFullScreenView = (
    <Dialog open={showFullView && isMobile} onOpenChange={(open) => !open && handleBackClick()}>
      <DialogContentBare
        motion="right"
        overlayClassName="z-takeover"
        className="fixed inset-0 z-takeover flex flex-col bg-frost-card"
      >
        {/* Header */}
        {/* Como la maqueta: 20px a los costados, flecha de 18px y 70px de alto (el botón
            mide 36 a la vista y 44 al toque para no estirar el encabezado). */}
        <div className="flex items-center gap-1 border-b border-[rgba(var(--f-ink-rgb),0.08)] bg-frost-card px-5 pt-[18px] pb-3.5">
          <button
            type="button"
            onClick={handleBackClick}
            aria-label="Volver"
            className={`relative -ml-2.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-frost-ink-soft transition-colors hover:bg-[rgba(var(--f-ink-rgb),0.05)] ${TOUCH_HIT_AREA}`}
          >
            <NavIcon name="arrow-left" className="text-fs-18" />
          </button>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="frost-mono text-fs-10 font-semibold uppercase leading-[1.35] tracking-[0.22em] text-frost-ink-whisper">
              Tu bandeja
            </span>
            <DialogTitleBare asChild>
              <h1 className="text-fs-16 font-bold leading-[1.35] tracking-[-0.01em] text-frost-ink">
                Notificaciones
                <span className="text-frost-brand">.</span>
              </h1>
            </DialogTitleBare>
          </div>
          {/* El total como texto, sin pastilla. */}
          {totalCount > 0 && (
            <span className="ml-auto text-fs-13 font-medium tabular-nums text-frost-ink-mid">
              {totalCount}
            </span>
          )}
        </div>

        {/* List */}
        <div className="scrollbar-auto-hide flex-1 overflow-y-auto">
          {hasAnyNotifications ? (
            <ul className="bg-transparent">{renderList('sheet')}</ul>
          ) : (
            <ul>
              <NotificationEmptyState variant="sheet" />
            </ul>
          )}
        </div>

        {selectedBookingId && (
          <ActionDialog bookingId={selectedBookingId} onClose={handleCloseSideMenu} refetch={() => {}} />
        )}
        {showFullView && professionalReviewHost}

        <style>{`
              @keyframes notif-row-in {
                from { opacity: 0; transform: translateY(4px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
      </DialogContentBare>
    </Dialog>
  );

  return (
    <>
      {/* Trigger */}
      {isMobile ? (
        withRailTooltip(
          <button
            type="button"
            onClick={handleNotificationClick}
            aria-label={`Notificaciones${totalUnreadCount ? `, ${totalUnreadCount} sin leer` : ''}`}
            className={triggerButtonClass}
          >
            {renderTriggerInner()}
          </button>
        )
      ) : (
        // Panel no modal: Popover (role="dialog") en vez de un menú. Con DropdownMenu era
        // role="menu" sin ítems de menú, Tab cerraba el panel y no se llegaba a
        // "Ver todas las notificaciones" ni a archivar con el teclado, y la página de
        // atrás quedaba bloqueada. Afuera o Esc lo cierran, igual que antes.
        <PopoverPrimitive.Root open={showDropdownMenu} onOpenChange={handleDropdownOpen}>
          {withRailTooltip(
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                aria-label={`Notificaciones${totalUnreadCount ? `, ${totalUnreadCount} sin leer` : ''}`}
                className={triggerButtonClass}
              >
                {renderTriggerInner()}
              </button>
            </PopoverPrimitive.Trigger>
          )}

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              side={isRail ? 'right' : 'bottom'}
              // Desde el riel: 14px más allá de su borde, como la maqueta (la campana queda 12px
              // adentro, expandido o contraído).
              sideOffset={isRail ? 26 : 12}
              align={isRail ? 'start' : 'end'}
              // Flota: nunca se pega al borde de la ventana.
              collisionPadding={12}
              // Como hacía el menú: el foco va al panel (no al primer botón), Tab entra a la lista.
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                panelRef.current?.focus({ preventScroll: true });
              }}
              ref={panelRef}
              // Mismas clases que ponía DropdownMenuContent + las del panel: se ve igual.
              // Panel flotante: blanco liso (lo de atrás no se transparenta), radio 16, una sombra y sin borde.
              className="z-sheet flex w-[392px] max-h-(--radix-popover-content-available-height) flex-col overflow-hidden rounded-2xl bg-frost-card text-frost-ink shadow-[0_2px_6px_rgba(var(--f-ink-rgb),0.06),0_24px_56px_-22px_rgba(var(--f-ink-rgb),0.42)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 duration-200 ease-frost data-[state=closed]:ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:animate-none focus:outline-none"
            >
              <NotificationHeader
                hasNotifications={hasAnyNotifications}
                unreadCount={totalUnreadCount}
                onMarkAllAsRead={() => {
                  markAllWebSocketAsRead();
                  markAllApiNotificationsAsRead();
                }}
              />

              <ul className="scrollbar-auto-hide min-h-[80px] max-h-[430px] overflow-y-auto">
                {hasAnyNotifications ? renderList('dropdown') : <NotificationEmptyState />}
              </ul>

              {hasAnyNotifications && (
                <div className="border-t border-[rgba(var(--f-ink-rgb),0.08)] bg-transparent px-3 py-[9px]">
                  <button
                    type="button"
                    onClick={() => {
                      if (isMobile) {
                        setShowFullView(true);
                      } else {
                        navigate('/notificaciones');
                        setShowDropdownMenu(false);
                      }
                    }}
                    // Link azul sin caja.
                    className="group/cta flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-fs-13 font-semibold text-frost-brand transition-colors hover:bg-frost-brand-wash focus:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30"
                  >
                    <span>Ver todas las notificaciones</span>
                    <NavIcon
                      name="arrow-right"
                      className="text-fs-10 transition-transform group-hover/cta:translate-x-0.5"
                    />
                  </button>
                </div>
              )}

              <style>{`
              @keyframes notif-row-in {
                from { opacity: 0; transform: translateY(4px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}

      {showConfirmation && (
        <Suspense fallback={null}>
          <ConfirmationModal
            {...confirmationItems}
            isOpen={showConfirmation}
            setIsOpen={setShowConfirmation}
            onConfirm={() => mutation.mutate()}
          />
        </Suspense>
      )}

      {selectedBookingId && !showFullView && (
        <ActionDialog bookingId={selectedBookingId} onClose={handleCloseSideMenu} refetch={() => {}} />
      )}
      {!showFullView && professionalReviewHost}

      {mobileFullScreenView}
    </>
  );
};
